import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Pagination, Stack, Typography, Chip, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Switch, FormControlLabel, TextField, MenuItem, Checkbox, Alert, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchAllSurveys, deleteSurvey as deleteSurveyAction, publishSurvey as publishSurveyAction, createSurvey } from '../controllers/surveysController';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';

// Helper function to format UTC date without timezone conversion
const formatUTCDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const QuestionTypeLabels = {
  0: 'Tek Seçim',
  1: 'Çoklu Seçim',
  2: 'Kısa Metin',
  3: 'Uzun Metin',
  4: 'Değerlendirme (1-5)',
  5: 'Tarih',
  6: 'Sayı',
};

const SurveysList = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data, meta } = await fetchAllSurveys({ pageNumber: page, pageSize });
        if (!active) return;
        setItems(data || []);
        setTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Anketler alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, pageSize]);

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `temp-${Date.now()}`,
      questionText: '',
      questionType: 0,
      isRequired: false,
      order: questions.length,
      options: [{ id: `temp-opt-${Date.now()}`, optionText: '', order: 0 }],
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleUpdateQuestion = (questionId, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          if (field === 'questionType') {
            const newType = Number(value);
            return {
              ...q,
              questionType: newType,
              options: newType === 0 || newType === 1 ? (q.options.length > 0 ? q.options : [{ id: `temp-opt-${Date.now()}`, optionText: '', order: 0 }]) : [],
            };
          }
          return { ...q, [field]: value };
        }
        return q;
      })
    );
  };

  const handleDeleteQuestion = (questionId) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId).map((q, idx) => ({ ...q, order: idx })));
  };

  const handleMoveQuestion = (questionId, direction) => {
    const index = questions.findIndex((q) => q.id === questionId);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === questions.length - 1) return;

    const newQuestions = [...questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    const reorderedQuestions = newQuestions.map((q, idx) => ({ ...q, order: idx }));
    setQuestions(reorderedQuestions);
  };

  const handleAddOption = (questionId) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: [...q.options, { id: `temp-opt-${Date.now()}`, optionText: '', order: q.options.length }],
          };
        }
        return q;
      })
    );
  };

  const handleUpdateOption = (questionId, optionId, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map((opt) => (opt.id === optionId ? { ...opt, optionText: value } : opt)),
          };
        }
        return q;
      })
    );
  };

  const handleDeleteOption = (questionId, optionId) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === questionId) {
          const newOptions = q.options.filter((opt) => opt.id !== optionId);
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const handleCreateSurvey = async () => {
    setCreateError('');
    if (!surveyForm.title.trim()) {
      setCreateError('Anket başlığı zorunludur');
      return;
    }

    const validQuestions = questions.filter((q) => q.questionText.trim());
    if (validQuestions.length === 0) {
      setCreateError('En az bir soru eklemeniz gerekmektedir');
      return;
    }

    for (const q of validQuestions) {
      if (!q.questionText.trim()) {
        setCreateError('Tüm soruların metni doldurulmalıdır');
        return;
      }
      if ((q.questionType === 0 || q.questionType === 1) && q.options.filter((opt) => opt.optionText.trim()).length < 2) {
        setCreateError(`${q.questionText} sorusu için en az 2 seçenek eklemeniz gerekmektedir`);
        return;
      }
    }

    setSaving(true);
    try {
      const toIsoString = (datetimeLocal) => {
        if (!datetimeLocal) return null;
        return `${datetimeLocal}:00.000Z`;
      };

      const payload = {
        title: surveyForm.title.trim(),
        description: surveyForm.description.trim() || null,
        startDate: toIsoString(surveyForm.startDate),
        endDate: toIsoString(surveyForm.endDate),
        isActive: surveyForm.isActive,
        questions: validQuestions.map((q, idx) => {
          const questionPayload = {
            questionText: q.questionText.trim(),
            questionType: Number(q.questionType),
            isRequired: q.isRequired,
            order: q.order !== undefined ? q.order : idx,
          };

          if (q.questionType === 0 || q.questionType === 1) {
            questionPayload.options = q.options
              .filter((opt) => opt.optionText.trim())
              .map((opt, optIdx) => ({
                optionText: opt.optionText.trim(),
                order: opt.order !== undefined ? opt.order : optIdx,
              }));
          } else {
            questionPayload.options = [];
          }

          return questionPayload;
        }),
      };

      await createSurvey(payload);
      setCreateOpen(false);
      setSurveyForm({ title: '', description: '', startDate: '', endDate: '', isActive: true });
      setQuestions([]);
      setCreateError('');
      // Refresh list
      const { data, meta } = await fetchAllSurveys({ pageNumber: page, pageSize });
      setItems(data || []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      setCreateError(e?.message || 'Anket oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setSurveyForm({ title: '', description: '', startDate: '', endDate: '', isActive: true });
    setQuestions([]);
    setCreateError('');
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Anketler</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': {
              backgroundColor: '#CFDBEA',
              boxShadow: 'none',
            },
          }}
          onClick={() => setCreateOpen(true)}
        >
          Yeni Anket
        </Button>
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
          border: '1px solid #f5f5f5',
          py: 1,
          px: 2.5,
        }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: 'transparent' }}>
            <TableRow>
              <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Başlık</TableCell>
              <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3', maxWidth: 300 }}>Açıklama</TableCell>
              <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Soru Sayısı</TableCell>
              <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Başlangıç Tarihi</TableCell>
              <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Bitiş Tarihi</TableCell>
              <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
              <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, fontSize: '14px', color: '#a3a3a3' }}>
                  Henüz anket bulunmamaktadır.
                </TableCell>
              </TableRow>
            ) : (
              items.map((survey, index) => (
                <TableRow
                  key={survey.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '& .MuiTableCell-root': {
                      borderBottom: index === items.length - 1 ? 'none' : '1px dashed #e8e8e8',
                      px: 1.5,
                      py: 1.5,
                      color: '#444444',
                      fontWeight: 500,
                    },
                    '&:hover': {
                      backgroundColor: '#fafafa',
                    },
                  }}
                  onClick={() => navigate(`/surveys/${survey.id}`)}
                >
                <TableCell>{survey.title}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.5,
                      }}
                    >
                      {survey.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={survey.questionCount || 0}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor: '#E7EDF4',
                        color: '#0E4D94',
                        border: 'none',
                      }}
                    />
                  </TableCell>
                <TableCell>{formatUTCDateTime(survey.startDate)}</TableCell>
                <TableCell>{formatUTCDateTime(survey.endDate)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={survey.isActive || false}
                        onChange={async (e) => {
                          const newActive = e.target.checked;
                          const next = new Set(togglingIds);
                          next.add(survey.id);
                          setTogglingIds(next);
                          try {
                            await publishSurveyAction(survey.id, newActive);
                            const { data, meta } = await fetchAllSurveys({ pageNumber: page, pageSize });
                            setItems(data || []);
                            setTotalPages(meta?.totalPages || 1);
                          } catch (err) {
                            console.error('Yayın durumu güncellenemedi:', err);
                          } finally {
                            next.delete(survey.id);
                            setTogglingIds(new Set(next));
                          }
                        }}
                        disabled={togglingIds.has(survey.id)}
                        size="small"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {togglingIds.has(survey.id) ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Chip
                            label={survey.isActive ? 'Aktif' : 'Pasif'}
                            size="small"
                              sx={{
                                backgroundColor: survey.isActive ? '#E7EDF4' : '#FFF4E6',
                                color: survey.isActive ? '#0E4D94' : '#FF9800',
                                fontSize: '11px',
                                fontWeight: 600,
                                height: '22px',
                                borderRadius: '12px',
                              }}
                          />
                        )}
                      </Stack>
                    }
                    sx={{ margin: 0 }}
                  />
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="Cevapları Görüntüle">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/surveys/${survey.id}/responses`)}
                          sx={{
                            backgroundColor: '#E7EDF4',
                            borderRadius: '6px',
                            width: 32,
                            height: 32,
                            '&:hover': {
                              backgroundColor: '#CFDBEA',
                            },
                            '& svg': {
                              fontSize: '14px',
                              color: '#0E4D94',
                            },
                          }}
                        >
                          <AssessmentIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Görüntüle">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/surveys/${survey.id}`)}
                          sx={{
                            backgroundColor: '#E7EDF4',
                            borderRadius: '6px',
                            width: 32,
                            height: 32,
                            '&:hover': {
                              backgroundColor: '#CFDBEA',
                            },
                            '& svg': {
                              fontSize: '14px',
                              color: '#0E4D94',
                            },
                          }}
                        >
                          <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          onClick={() => { setSelected(survey); setConfirmOpen(true); }}
                          sx={{
                            backgroundColor: '#FFEEF3',
                            borderRadius: '6px',
                            width: 32,
                            height: 32,
                            '&:hover': {
                              backgroundColor: '#FFD6E0',
                            },
                            '& svg': {
                              fontSize: '14px',
                              color: '#F8285A',
                            },
                          }}
                        >
                          <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                    </Stack>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
      </Box>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Anketi Sil
          <IconButton
            onClick={() => setConfirmOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {selected ? `"${selected.title}" anketini silmek istediğinize emin misiniz?` : 'Seçili anketi silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
            if (!selected) return;
            try {
              await deleteSurveyAction(selected.id);
              setConfirmOpen(false);
              setSelected(null);
              const { data, meta } = await fetchAllSurveys({ pageNumber: page, pageSize });
              setItems(data || []);
              setTotalPages(meta?.totalPages || 1);
            } catch (e) {}
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#E01E4A',
              },
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Survey Dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Yeni Anket Oluştur
          <IconButton
            onClick={handleCloseCreate}
            size="small"
            disabled={saving}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={3}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError('')}>
                {createError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none' }}>
              <Stack spacing={2.5}>
                <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                  Anket Bilgileri
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                    Anket Başlığı
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    value={surveyForm.title}
                    onChange={(e) => setSurveyForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder=""
                    disabled={saving}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                    Açıklama
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={surveyForm.description}
                    onChange={(e) => setSurveyForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Anket hakkında açıklama (opsiyonel)"
                    disabled={saving}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                        px: 0,
                        py: 0,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Başlangıç Tarihi
                    </Typography>
                    <TextField
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={surveyForm.startDate}
                      onChange={(e) => setSurveyForm((f) => ({ ...f, startDate: e.target.value }))}
                      disabled={saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                      Bitiş Tarihi
                    </Typography>
                    <TextField
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={surveyForm.endDate}
                      onChange={(e) => setSurveyForm((f) => ({ ...f, endDate: e.target.value }))}
                      disabled={saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0E4D94',
                          },
                        },
                        '& .MuiInputBase-input': {
                          py: 1.2,
                          px: 1.5,
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e6e6e6',
                        },
                      }}
                    />
                  </Box>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={surveyForm.isActive}
                      onChange={(e) => setSurveyForm((f) => ({ ...f, isActive: e.target.checked }))}
                      disabled={saving}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0E4D94',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0E4D94',
                        },
                      }}
                    />
                  }
                  label="Anket aktif olsun"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      ml: 1,
                    },
                  }}
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none' }}>
              <Stack spacing={2.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
                    Sorular ({questions.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    onClick={handleAddQuestion}
                    disabled={saving}
                    sx={{
                      textTransform: 'none',
                      fontSize: 13,
                      borderRadius: '5px',
                      borderColor: '#0E4D94',
                      color: '#0E4D94',
                      '&:hover': {
                        borderColor: '#0a3d7a',
                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                      },
                    }}
                  >
                    Soru Ekle
                  </Button>
                </Stack>

                {questions.length === 0 ? (
                  <Alert severity="info" sx={{ fontSize: '13px' }}>
                    Henüz soru eklenmemiş. "Soru Ekle" butonuna tıklayarak soru ekleyebilirsiniz.
                  </Alert>
                ) : (
                  <Stack spacing={3}>
                    {questions.map((question, index) => (
                      <Card 
                        key={question.id} 
                        sx={{ 
                          border: '1px solid #e6e6e6', 
                          boxShadow: 'none', 
                          borderRadius: '8px',
                          backgroundColor: '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.08)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Stack spacing={2.5}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1.5, borderBottom: '1px solid #e6e6e6' }}>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Chip 
                                  label={`Soru ${index + 1}`} 
                                  size="small" 
                                  sx={{ 
                                    height: 28, 
                                    fontSize: '12px', 
                                    fontWeight: 600,
                                    backgroundColor: '#0E4D94',
                                    color: '#ffffff',
                                  }} 
                                />
                                <Chip 
                                  label={QuestionTypeLabels[question.questionType] || 'Bilinmeyen'} 
                                  size="small" 
                                  sx={{ 
                                    height: 24, 
                                    fontSize: '11px', 
                                    fontWeight: 500, 
                                    backgroundColor: '#E7EDF4', 
                                    color: '#0E4D94' 
                                  }} 
                                />
                                {question.isRequired && (
                                  <Chip 
                                    label="Zorunlu" 
                                    size="small" 
                                    sx={{ 
                                      height: 24, 
                                      fontSize: '11px', 
                                      fontWeight: 500, 
                                      backgroundColor: '#FFEEF3', 
                                      color: '#F8285A' 
                                    }} 
                                  />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title="Yukarı Taşı">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMoveQuestion(question.id, 'up')}
                                    disabled={index === 0 || saving}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      backgroundColor: '#E7EDF4',
                                      '&:hover': {
                                        backgroundColor: '#CFDBEA',
                                      },
                                      '& svg': {
                                        fontSize: '14px',
                                        color: '#0E4D94',
                                      },
                                    }}
                                  >
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Aşağı Taşı">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMoveQuestion(question.id, 'down')}
                                    disabled={index === questions.length - 1 || saving}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      backgroundColor: '#E7EDF4',
                                      '&:hover': {
                                        backgroundColor: '#CFDBEA',
                                      },
                                      '& svg': {
                                        fontSize: '14px',
                                        color: '#0E4D94',
                                      },
                                    }}
                                  >
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Soruyu Sil">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteQuestion(question.id)}
                                    disabled={saving}
                                    sx={{
                                      backgroundColor: '#FFEEF3',
                                      width: 28,
                                      height: 28,
                                      '&:hover': {
                                        backgroundColor: '#FFD6E0',
                                      },
                                      '& svg': {
                                        fontSize: '14px',
                                        color: '#F8285A',
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>

                            <Box>
                              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                                Soru Metni
                              </Typography>
                              <TextField
                                fullWidth
                                required
                                multiline
                                minRows={2}
                                value={question.questionText}
                                onChange={(e) => handleUpdateQuestion(question.id, 'questionText', e.target.value)}
                                placeholder="Soruyu buraya yazın..."
                                disabled={saving}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#0E4D94',
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                    px: 0,
                                    py: 0,
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                  },
                                }}
                              />
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '14px' }}>
                                  Soru Tipi
                                </Typography>
                                <TextField
                                  select
                                  fullWidth
                                  value={question.questionType}
                                  onChange={(e) => handleUpdateQuestion(question.id, 'questionType', e.target.value)}
                                  disabled={saving}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      py: 1.2,
                                      px: 1.5,
                                      fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#e6e6e6',
                                    },
                                  }}
                                >
                                  {Object.entries(QuestionTypeLabels).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                      {label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={question.isRequired}
                                      onChange={(e) => handleUpdateQuestion(question.id, 'isRequired', e.target.checked)}
                                      disabled={saving}
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#0E4D94',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#0E4D94',
                                        },
                                      }}
                                    />
                                  }
                                  label="Zorunlu Soru"
                                  sx={{
                                    '& .MuiFormControlLabel-label': {
                                      fontSize: '13px',
                                      ml: 1,
                                    },
                                  }}
                                />
                              </Box>
                            </Stack>

                            {(question.questionType === 0 || question.questionType === 1) && (
                              <Box sx={{ pt: 1.5, borderTop: '1px solid #e6e6e6' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                                    Seçenekler
                                  </Typography>
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon sx={{ width: 14 }} />}
                                    onClick={() => handleAddOption(question.id)}
                                    disabled={saving}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '12px',
                                      borderRadius: '5px',
                                      border: '1px dashed #e6e6e6',
                                      color: '#666666',
                                      '&:hover': {
                                        borderColor: '#0E4D94',
                                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                                        color: '#0E4D94',
                                      },
                                    }}
                                  >
                                    Seçenek Ekle
                                  </Button>
                                </Stack>
                                <Box sx={{ backgroundColor: '#ffffff', borderRadius: '8px', p: 2, border: '1px solid #e6e6e6' }}>
                                  <Stack spacing={1.5}>
                                    {question.options.map((option, optIdx) => (
                                      <Stack key={option.id} direction="row" spacing={1.5} alignItems="center">
                                        <Chip
                                          label={optIdx + 1}
                                          size="small"
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            backgroundColor: '#0E4D94',
                                            color: '#ffffff',
                                            flexShrink: 0,
                                          }}
                                        />
                                        <TextField
                                          fullWidth
                                          size="small"
                                          value={option.optionText}
                                          onChange={(e) => handleUpdateOption(question.id, option.id, e.target.value)}
                                          placeholder="Seçenek metnini girin..."
                                          disabled={saving}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: '8px',
                                              backgroundColor: '#fafafa',
                                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                              },
                                            },
                                            '& .MuiInputBase-input': {
                                              py: 1.2,
                                              px: 1.5,
                                              fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                              borderColor: '#e6e6e6',
                                            },
                                          }}
                                        />
                                        {question.options.length > 1 && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteOption(question.id, option.id)}
                                            disabled={saving}
                                            sx={{
                                              backgroundColor: '#FFEEF3',
                                              width: 32,
                                              height: 32,
                                              flexShrink: 0,
                                              '&:hover': {
                                                backgroundColor: '#FFD6E0',
                                              },
                                              '& svg': {
                                                fontSize: '16px',
                                                color: '#F8285A',
                                              },
                                            }}
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      </Stack>
                                    ))}
                                    {question.options.filter((opt) => opt.optionText.trim()).length < 2 && (
                                      <Alert severity="warning" sx={{ fontSize: '13px', mt: 1 }}>
                                        Bu soru tipi için en az 2 seçenek eklemeniz gerekmektedir.
                                      </Alert>
                                    )}
                                  </Stack>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={handleCloseCreate}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ width: 16 }} />}
            onClick={handleCreateSurvey}
            disabled={saving || !surveyForm.title.trim() || questions.filter((q) => q.questionText.trim()).length === 0}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {saving ? 'Oluşturuluyor...' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveysList;

